/**
 * Homepage — outcome-led remix with animated personality.
 *
 * Keeps the "Pick a goal, get a finished result" message at the centre, but
 * mixes it with the richer surface area of the old grid-of-tools homepage:
 * animated stats, tool-category grid, audience cards, social proof marquee,
 * testimonials, FAQ, and a deep-purple final CTA. All entrance animations
 * use framer-motion with `whileInView` so they reveal as you scroll.
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Wand2,
  Layers,
  Rocket,
  Plus,
  Search,
  Youtube,
  FileText,
  Share2,
  Palette,
  Megaphone,
  GraduationCap,
  Zap,
  CheckCircle2,
  PlayCircle,
  Hash,
  Image as ImageIcon,
  Star,
  Globe,
  Type,
  Link as LinkIcon,
  Key,
  Hash as HashIcon,
  Calculator,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./HomePage.css";

// ── Static data ────────────────────────────────────────────────────────────

const STATS = [
  { value: 84, suffix: "+", label: "AI Tools" },
  { value: 9, suffix: "", label: "Workflows" },
  { value: 10, suffix: "K+", label: "Creators" },
  { value: 4.9, suffix: "★", label: "Rated" },
];

const CATEGORIES = [
  { key: "seo", title: "SEO", count: "14 tools", Icon: Search, to: "/tools/seo" },
  { key: "youtube", title: "YouTube", count: "12 tools", Icon: Youtube, to: "/tools/youtube" },
  { key: "content", title: "Content", count: "16 tools", Icon: FileText, to: "/tools/content" },
  { key: "social", title: "Social Media", count: "11 tools", Icon: Share2, to: "/tools/social-media" },
  { key: "design", title: "Design & PDF", count: "18 tools", Icon: Palette, to: "/tools/design" },
  { key: "ads", title: "Ads & Marketing", count: "9 tools", Icon: Megaphone, to: "/tools" },
  { key: "education", title: "Education", count: "AI lessons", Icon: GraduationCap, to: "/workflows" },
  { key: "more", title: "Browse all", count: "84+ total", Icon: Layers, to: "/tools" },
];

const STEPS = [
  { n: "01", t: "Tell us your goal, once.", d: "Pick what you want to do and fill one short form — your idea, audience, and tone." },
  { n: "02", t: "We run the whole workflow for you.", d: "MarkeeMix runs the right tools in the background and passes your work from one to the next automatically." },
  { n: "03", t: "Get your finished package — and tweak anything.", d: "Everything lands on one page. Edit or regenerate any piece with one click." },
];

const AUDIENCES = [
  { title: "Creators & YouTubers", line: "Video idea se publish-ready package — minutes mein.", emoji: "🎬" },
  { title: "Teachers & Educators", line: "Topic ko lessons, slides, aur captioned content mein turn karein.", emoji: "🎓" },
  { title: "Businesses", line: "Google par rank karein aur agency hire kiye bina ads chalayein.", emoji: "🏢" },
  { title: "Agencies & Freelancers", line: "Client work fast, white-labeled, aapke brand ke under.", emoji: "🤝" },
];

const FREE_TOOLS = [
  { slug: "word-counter", title: "Word Counter", desc: "Words, characters, sentences & reading time.", Icon: Type, color: "#7C3AED" },
  { slug: "url-shortener", title: "URL Shortener", desc: "Shorten long URLs with click analytics.", Icon: LinkIcon, color: "#EC4899" },
  { slug: "password-generator", title: "Password Generator", desc: "Strong, secure, random passwords.", Icon: Key, color: "#F97316" },
  { slug: "hashtag-generator", title: "Hashtag Generator", desc: "Trending hashtags for any niche.", Icon: HashIcon, color: "#10B981" },
  { slug: "roi-calculator", title: "ROI Calculator", desc: "Marketing campaign ROI in seconds.", Icon: Calculator, color: "#3B82F6" },
  { slug: "color-palette-generator", title: "Color Palette", desc: "Brand colors + complementary palettes.", Icon: Palette, color: "#F59E0B" },
];


const MARQUEE_TAGS = [
  { Icon: PlayCircle, label: "AutoCaptions (NEW)" },
  { Icon: Search, label: "Keyword Research" },
  { Icon: Youtube, label: "YouTube Title Generator" },
  { Icon: FileText, label: "AI Article Writer" },
  { Icon: Share2, label: "Caption Generator" },
  { Icon: Hash, label: "Hashtag Pack" },
  { Icon: Palette, label: "Logo Maker" },
  { Icon: ImageIcon, label: "Thumbnail Designer" },
  { Icon: Megaphone, label: "Ad Copy Generator" },
  { Icon: Sparkles, label: "AI Slide Generator" },
  { Icon: Globe, label: "Site Audit" },
];

const TESTIMONIALS = [
  {
    quote: "I publish 3x faster. The YouTube workflow gave me titles, thumbnail concepts, full script, and shorts — all in one go. Game-changer for solo creators.",
    name: "Ravi Sharma",
    role: "YouTube Educator · 240K subs",
    initials: "RS",
  },
  {
    quote: "Pehle 5 tools alag-alag kholne padte the. Ab ek goal pick karta hu, MarkeeMix sab kuch automatic kar deta hai. Client deliverables 60% jaldi go out.",
    name: "Priya Kulkarni",
    role: "Founder, Glow Marketing",
    initials: "PK",
  },
  {
    quote: "The SEO action plan workflow saved me from buying 3 separate tools. Real audit data, real keyword research, real content briefs. Best value on the market.",
    name: "Daniel Lee",
    role: "Affiliate marketer · 7-figure",
    initials: "DL",
  },
];

const FAQS = [
  {
    q: "Do I need to learn each tool individually?",
    a: "Nope. Workflows pick the right tools for your goal and run them in the right order. You just describe what you want — MarkeeMix handles the rest. The 84+ individual tools are still there if you want to use them directly.",
  },
  {
    q: "Is there a free plan?",
    a: "Yes — Workflows, the major AI tools, and the full results editor are free to try. Paid plans unlock higher generation limits, advanced exports, and team features.",
  },
  {
    q: "Whose AI is doing the work?",
    a: "We orchestrate top-tier models (OpenAI GPT, Anthropic Claude, Gemini Nano Banana for images) behind a single key. You don't manage API keys or rate limits — just run workflows.",
  },
  {
    q: "Can I edit the AI output?",
    a: "Every block on the results page has Copy, Edit, and Regenerate. Edit any output inline, regenerate a section, or copy it straight into your tool of choice.",
  },
  {
    q: "Is MarkeeMix suitable for agencies?",
    a: "Absolutely. Our 'For Agencies' page covers white-label workflows, client-ready deliverables, and team workspaces. Most agencies replace 3-5 SaaS subscriptions with MarkeeMix.",
  },
];

// ── Animated stat counter ──────────────────────────────────────────────────
function StatCounter({ value, suffix }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const isFloat = !Number.isInteger(value);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1400;
    const startTime = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(isFloat ? +(value * eased).toFixed(1) : Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, isFloat]);

  return (
    <span ref={ref} className="hp-stat-value">
      <em>{display}{suffix}</em>
    </span>
  );
}

// ── Reusable scroll-reveal wrapper ─────────────────────────────────────────
const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, ease: [0.2, 0.7, 0.2, 1], delay },
});

const float = (delay = 0, y = 8) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.2, 0.7, 0.2, 1] },
});

// ── Page ───────────────────────────────────────────────────────────────────
export default function HomePage() {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState(0);
  const goWorkflows = () => navigate("/workflows");
  const goTools = () => navigate("/tools");

  return (
    <>
      <Header />
      <div className="hp" data-testid="home-outcome-led">
        {/* animated background orbs */}
        <span className="hp-orb hp-orb--purple" />
        <span className="hp-orb hp-orb--peach" />
        <span className="hp-orb hp-orb--mid" />

        {/* ── HERO ── */}
        <section className="hp-hero">
          <div className="hp-inner">
            <div className="hp-hero-grid">
              <div>
                <motion.span className="hp-eyebrow" data-testid="home-eyebrow" {...float(0)}>
                  <span className="hp-eyebrow-dot" /> All-in-One marketing tools platform
                </motion.span>
                <motion.h1 className="hp-h1" data-testid="home-headline" {...float(0.08)}>
                  Pick a goal.<br /><em>Get a finished result.</em>
                </motion.h1>
                <motion.p className="hp-sub" data-testid="home-subhead" {...float(0.16)}>
                  MarkeeMix aapke idea ko complete marketing output mein turn karta hai —
                  titles, scripts, thumbnails, articles, ads, aur much more — by running
                  the right AI tools in the right order. <strong>No more jumping between 84 tools.</strong>
                </motion.p>
                <motion.div className="hp-cta-row" {...float(0.24)}>
                  <button className="hp-cta-primary" onClick={goWorkflows} data-testid="home-primary-cta">
                    <Sparkles size={16} /> Start a Workflow — Free
                  </button>
                  <button className="hp-cta-secondary" onClick={goTools} data-testid="home-secondary-cta">
                    Browse All Tools <ArrowRight size={14} />
                  </button>
                </motion.div>
                <motion.div className="hp-hero-meta" {...float(0.32)} data-testid="home-hero-meta">
                  <span className="hp-hero-meta-item"><CheckCircle2 size={14} color="#15803d" /> No credit card</span>
                  <span className="hp-hero-meta-item"><CheckCircle2 size={14} color="#15803d" /> 84+ AI tools</span>
                  <span className="hp-hero-meta-item"><CheckCircle2 size={14} color="#15803d" /> Free to try</span>
                </motion.div>
              </div>

              {/* Floating preview cards illustrating "package" output */}
              <div className="hp-hero-preview" aria-hidden="true">
                <motion.div
                  className="hp-float-card"
                  data-pos="1"
                  initial={{ opacity: 0, y: 16, rotate: -3 }}
                  animate={{ opacity: 1, y: [0, -6, 0], rotate: -3 }}
                  transition={{ y: { duration: 4, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.6, delay: 0.2 } }}
                >
                  <div className="hp-float-card-icon"><PlayCircle size={18} /></div>
                  <div>
                    <p className="hp-float-card-title">Hook + Full Script</p>
                    <p className="hp-float-card-sub">Auto-generated for your video</p>
                  </div>
                </motion.div>
                <motion.div
                  className="hp-float-card"
                  data-pos="2"
                  initial={{ opacity: 0, y: 16, rotate: 2 }}
                  animate={{ opacity: 1, y: [0, -8, 0], rotate: 2 }}
                  transition={{ y: { duration: 5.2, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.6, delay: 0.35 } }}
                >
                  <div className="hp-float-card-icon"><ImageIcon size={18} /></div>
                  <div>
                    <p className="hp-float-card-title">Thumbnail Concepts</p>
                    <p className="hp-float-card-sub">3 variants ready to design</p>
                  </div>
                </motion.div>
                <motion.div
                  className="hp-float-card"
                  data-pos="3"
                  initial={{ opacity: 0, y: 16, rotate: -2 }}
                  animate={{ opacity: 1, y: [0, -7, 0], rotate: -2 }}
                  transition={{ y: { duration: 4.6, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.6, delay: 0.5 } }}
                >
                  <div className="hp-float-card-icon"><Hash size={18} /></div>
                  <div>
                    <p className="hp-float-card-title">SEO Tags & Hashtags</p>
                    <p className="hp-float-card-sub">Copy-ready pack</p>
                  </div>
                </motion.div>
                <motion.div
                  className="hp-float-card"
                  data-pos="4"
                  initial={{ opacity: 0, y: 16, rotate: 3 }}
                  animate={{ opacity: 1, y: [0, -6, 0], rotate: 3 }}
                  transition={{ y: { duration: 4.2, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.6, delay: 0.65 } }}
                >
                  <div className="hp-float-card-icon"><Share2 size={18} /></div>
                  <div>
                    <p className="hp-float-card-title">Repurpose Pack</p>
                    <p className="hp-float-card-sub">Insta + X + LinkedIn captions</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* ── STATS ── */}
        <div className="hp-inner">
          <motion.div className="hp-stats" {...reveal()} data-testid="home-stats">
            {STATS.map((s, i) => (
              <div key={i} className="hp-stat" data-testid={`home-stat-${i}`}>
                <StatCounter value={s.value} suffix={s.suffix} />
                <div className="hp-stat-label">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ── MARQUEE — tool ribbon ── */}
        <div className="hp-inner">
          <div className="hp-marquee" data-testid="home-marquee">
            <div className="hp-marquee-track">
              {[...MARQUEE_TAGS, ...MARQUEE_TAGS].map((m, i) => (
                <div key={i} className="hp-marquee-chip">
                  <m.Icon size={15} /> {m.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <section className="hp-section">
          <div className="hp-inner">
            <motion.span className="hp-section-eyebrow" {...reveal()}>How it works</motion.span>
            <motion.h2 className="hp-section-title" {...reveal(0.05)} data-testid="home-how-title">
              Pick a goal. We do the running around.
            </motion.h2>
            <motion.p className="hp-section-sub" {...reveal(0.1)}>
              Tinkering ke bajaye, results par focus karein. Aapko sirf goal aur ek choti
              form bharni hai — baaki sab MarkeeMix sambhalta hai.
            </motion.p>
            <div className="hp-steps">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.n}
                  className="hp-step"
                  data-testid={`home-step-${s.n}`}
                  {...reveal(0.08 * i)}
                >
                  <div className="hp-step-num">{s.n}</div>
                  <h3 className="hp-step-title">{s.t}</h3>
                  <p className="hp-step-desc">{s.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CATEGORIES ── */}
        <section className="hp-section" style={{ paddingTop: 20 }}>
          <div className="hp-inner">
            <motion.span className="hp-section-eyebrow" {...reveal()}>Inside MarkeeMix</motion.span>
            <motion.h2 className="hp-section-title" {...reveal(0.05)} data-testid="home-cat-title">
              <em>84+</em> AI tools, organised by category.
            </motion.h2>
            <motion.p className="hp-section-sub" {...reveal(0.1)}>
              Workflows pe rahna preferred hai — par har individual tool bhi yahin hai,
              clean categories mein.
            </motion.p>
            <div className="hp-cats">
              {CATEGORIES.map((c, i) => (
                <motion.div
                  key={c.key}
                  className="hp-cat"
                  data-testid={`home-cat-${c.key}`}
                  onClick={() => navigate(c.to)}
                  {...reveal(0.05 * (i % 4))}
                >
                  <div className="hp-cat-icon"><c.Icon size={20} /></div>
                  <h3 className="hp-cat-title">{c.title}</h3>
                  <p className="hp-cat-count">{c.count}</p>
                  <ArrowRight size={16} className="hp-cat-arrow" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FREE TOOLS — 100% free utilities ── */}
        <section className="hp-section" style={{ paddingTop: 20 }} data-testid="home-free-tools-section">
          <div className="hp-inner">
            <motion.span className="hp-section-eyebrow" {...reveal()}>
              <Zap size={12} style={{ display: "inline", verticalAlign: "-2px", marginRight: 4, color: "#F97316" }}/> 100% FREE — no signup needed
            </motion.span>
            <motion.h2 className="hp-section-title" {...reveal(0.05)} data-testid="home-free-title">
              Quick free tools<em> — instant, no login.</em>
            </motion.h2>
            <motion.p className="hp-section-sub" {...reveal(0.1)}>
              15+ utility tools that work in your browser — word count, URL shortener, hashtags, ROI calculators, password generator and more.
            </motion.p>
            <div className="hp-free-grid" data-testid="home-free-tools-grid">
              {FREE_TOOLS.map((f, i) => (
                <motion.a
                  key={f.slug}
                  className="hp-free-card"
                  href={`/free/${f.slug}`}
                  data-testid={`home-free-tool-${f.slug}`}
                  {...reveal(0.04 * (i % 3))}
                >
                  <div className="hp-free-card-icon" style={{ background: f.color }}>
                    <f.Icon size={18} />
                  </div>
                  <div className="hp-free-card-body">
                    <p className="hp-free-card-title">{f.title}</p>
                    <p className="hp-free-card-desc">{f.desc}</p>
                  </div>
                  <ArrowRight size={14} className="hp-free-card-arrow" />
                </motion.a>
              ))}
            </div>
            <motion.div style={{ marginTop: 24, textAlign: "center" }} {...reveal(0.3)}>
              <button className="hp-cta-secondary" onClick={() => navigate("/free-tools")} data-testid="home-free-tools-all">
                Browse all 15+ free tools <ArrowRight size={14}/>
              </button>
            </motion.div>
          </div>
        </section>

        {/* ── YOUTUBE CRM SHOWCASE ── */}
        <section className="hp-section hp-section--crm" style={{ paddingTop: 32 }} data-testid="home-studio-crm-section">
          <div className="hp-inner">
            <motion.span className="hp-section-eyebrow" {...reveal()}>
              <Youtube size={12} style={{ display: "inline", verticalAlign: "-2px", marginRight: 4, color: "#7c3aed" }} /> NEW · YouTube Operations CRM
            </motion.span>
            <motion.h2 className="hp-section-title" {...reveal(0.05)} data-testid="home-crm-title">
              Run every YouTube channel <em>from one workspace.</em>
            </motion.h2>
            <motion.p className="hp-section-sub" {...reveal(0.1)}>
              MarkeeMix Studio brings agency-grade ops to YouTube — multi-channel pipelines,
              role-based dashboards, AI Topic Suggestions, automated WhatsApp + Email alerts,
              and one-click PDF reports for clients.
            </motion.p>
            <div className="hp-crm-grid" data-testid="home-crm-grid">
              {[
                { Icon: Wand2, t: "AI Topic Suggestions", d: "Claude Sonnet reads your channel + last 30 titles → 20 fresh topic ideas." },
                { Icon: Layers, t: "14-stage Kanban pipeline", d: "Idea → Script → Edit → Thumb → SEO → Schedule → Publish. Drag-and-drop." },
                { Icon: Megaphone, t: "WhatsApp + Email alerts", d: "10 Meta-approved templates. SLA breaches & assignments delivered instantly." },
                { Icon: Star, t: "Per-role dashboards", d: "Editor, Designer, SMM, Creator each see only what they own — cross-channel." },
                { Icon: FileText, t: "PDF reports for clients", d: "Pipeline, SLA, role-load and top videos exported in one click." },
                { Icon: Sparkles, t: "Setup checklist + AI niche", d: "Configure new channels in minutes — Claude pre-fills voice, keywords, schedule." },
              ].map((f, i) => (
                <motion.div key={f.t} className="hp-crm-card" data-testid={`home-crm-feature-${i}`} {...reveal(0.05 * (i % 3))}>
                  <div className="hp-crm-card-icon"><f.Icon size={20} /></div>
                  <h3>{f.t}</h3>
                  <p>{f.d}</p>
                </motion.div>
              ))}
            </div>
            <motion.div className="hp-cta-row" style={{ marginTop: 22 }} {...reveal(0.25)}>
              <button className="hp-cta-primary" onClick={() => navigate("/studio")} data-testid="home-crm-cta">
                <Youtube size={16} /> Open YouTube CRM
              </button>
              <button className="hp-cta-secondary" onClick={() => navigate("/agencies")} data-testid="home-agencies-cta">
                For agencies <ArrowRight size={14} />
              </button>
            </motion.div>
          </div>
        </section>

        {/* ── AUDIENCE ── */}
        <section className="hp-section" style={{ paddingTop: 20 }}>
          <div className="hp-inner">
            <motion.span className="hp-section-eyebrow" {...reveal()}>Who it's for</motion.span>
            <motion.h2 className="hp-section-title" {...reveal(0.05)} data-testid="home-audience-title">
              Built for everyone shipping marketing work.
            </motion.h2>
            <div className="hp-aud">
              {AUDIENCES.map((a, i) => (
                <motion.div
                  key={a.title}
                  className="hp-aud-card"
                  data-testid={`home-audience-${i}`}
                  {...reveal(0.07 * i)}
                >
                  <span className="hp-aud-emoji">{a.emoji}</span>
                  <h3 className="hp-aud-title">{a.title}</h3>
                  <p className="hp-aud-line">{a.line}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="hp-section" style={{ paddingTop: 20 }}>
          <div className="hp-inner">
            <motion.span className="hp-section-eyebrow" {...reveal()}>
              <Star size={12} style={{ display: "inline", verticalAlign: "-2px", marginRight: 4, color: "#fb923c" }} /> Loved by creators
            </motion.span>
            <motion.h2 className="hp-section-title" {...reveal(0.05)} data-testid="home-tests-title">
              From the people shipping with MarkeeMix.
            </motion.h2>
            <div className="hp-tests">
              {TESTIMONIALS.map((t, i) => (
                <motion.div key={i} className="hp-test" data-testid={`home-test-${i}`} {...reveal(0.07 * i)}>
                  <p className="hp-test-quote">"{t.quote}"</p>
                  <div className="hp-test-author">
                    <div className="hp-test-avatar">{t.initials}</div>
                    <div>
                      <p className="hp-test-name">{t.name}</p>
                      <p className="hp-test-role">{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="hp-section" style={{ paddingTop: 20 }}>
          <div className="hp-inner">
            <motion.span className="hp-section-eyebrow" {...reveal()}>FAQ</motion.span>
            <motion.h2 className="hp-section-title" {...reveal(0.05)} data-testid="home-faq-title">
              Quick answers.
            </motion.h2>
            <div className="hp-faq">
              {FAQS.map((f, i) => (
                <motion.div
                  key={i}
                  className="hp-faq-item"
                  data-open={faqOpen === i}
                  data-testid={`home-faq-${i}`}
                  {...reveal(0.04 * i)}
                >
                  <button
                    className="hp-faq-q"
                    onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                    aria-expanded={faqOpen === i}
                    data-testid={`home-faq-q-${i}`}
                  >
                    <span>{f.q}</span>
                    <Plus size={20} />
                  </button>
                  <div className="hp-faq-a">{f.a}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="hp-section" style={{ paddingTop: 20, paddingBottom: 120 }}>
          <div className="hp-inner">
            <motion.div className="hp-final" {...reveal()} data-testid="home-final-cta">
              <span className="hp-final-eyebrow"><Rocket size={12} style={{ display: "inline", verticalAlign: "-1px", marginRight: 4 }} /> Start in 30 seconds</span>
              <h2 className="hp-final-title">Stop juggling tools.<br />Start finishing work.</h2>
              <p className="hp-final-sub">
                One goal, one form. We&rsquo;ll deliver the package — script, copy, designs, plan.
                No credit card required.
              </p>
              <button className="hp-final-cta" onClick={goWorkflows} data-testid="home-final-cta-button">
                <Wand2 size={16} /> Start a Workflow — Free <ArrowRight size={16} />
              </button>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

// Suppress unused-imports for icons mentioned in JSDoc patterns
void Zap;
